// Client de las rutas públicas del wizard en retail-promotions-business (mismo patrón que Admin VL).
import { apiFetch } from '@/api/client';
import {
    isPromotionsConfigured,
    PromotionsServiceNotConfiguredError,
} from '@/domains/retail/admin/promotions/api/promotions.client';
import type {
    EvaluateSalePromotionsRequest,
    EvaluateSalePromotionsResponse,
    GetSaleCouponResponse,
    HoldSalePromotionRequest,
    HoldSalePromotionResponse,
    ListLiveReferralPromotionsResponse,
    ListStoreSalePromotionsResponse,
    ReferralTrigger,
    ReleaseSalePromotionResponse,
    SkuSalePromotionsResponse,
} from './wizardPromotionsContract';

function promotionsFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
    if (!isPromotionsConfigured()) return Promise.reject(new PromotionsServiceNotConfiguredError());
    const baseUrl: string = import.meta.env.VITE_API_RETAIL_PROMOTIONS_BASE_URL;
    return apiFetch<T>(path, { ...init, baseUrl });
}

function sessionPromotionPath(sessionId: string): string {
    return `/public/wizard/sessions/${encodeURIComponent(sessionId)}/sale-promotion`;
}

export const wizardPromotionsClient = {
    listStorePromotions: (sessionId: string): Promise<ListStoreSalePromotionsResponse> =>
        promotionsFetch(
            `/public/wizard/sale-promotions?${new URLSearchParams({ sessionId }).toString()}`,
        ),

    /** Promos de un SKU antes de apartarlo: la venta se evalúa con ese equipo como ancla. */
    listSkuPromotions: (sessionId: string, sku: string): Promise<SkuSalePromotionsResponse> =>
        promotionsFetch(
            `/public/wizard/sale-promotions/by-sku?${new URLSearchParams({ sessionId, sku }).toString()}`,
        ),

    /** Promos de referido vigentes; en una apertura sin venta solo las de ese `trigger`. */
    listLiveReferralPromotions: (
        sessionId: string,
        trigger?: ReferralTrigger,
    ): Promise<ListLiveReferralPromotionsResponse> =>
        promotionsFetch(
            `/public/wizard/referral-promotions?${new URLSearchParams({
                sessionId,
                ...(trigger && { trigger }),
            }).toString()}`,
        ),

    evaluate: (request: EvaluateSalePromotionsRequest): Promise<EvaluateSalePromotionsResponse> =>
        promotionsFetch('/public/wizard/sale-promotions/evaluate', {
            method: 'POST',
            body: JSON.stringify(request),
        }),

    hold: (
        sessionId: string,
        request: HoldSalePromotionRequest,
    ): Promise<HoldSalePromotionResponse> =>
        promotionsFetch(sessionPromotionPath(sessionId), {
            method: 'PUT',
            body: JSON.stringify(request),
        }),

    release: (sessionId: string): Promise<ReleaseSalePromotionResponse> =>
        promotionsFetch(sessionPromotionPath(sessionId), { method: 'DELETE' }),

    /** Sin `sessionId` es la lectura de una venta ya cerrada (Mis ventas, comprobante). */
    saleCoupon: (saleId: string, sessionId?: string): Promise<GetSaleCouponResponse> =>
        promotionsFetch(
            `/public/sales/${encodeURIComponent(saleId)}/coupon${
                sessionId ? `?${new URLSearchParams({ sessionId }).toString()}` : ''
            }`,
        ),

    /** Venta vista desde el detalle del admin; el admin de tenant manda la cadena de la venta. */
    adminSaleCoupon: (saleId: string, retailerId?: string): Promise<GetSaleCouponResponse> =>
        promotionsFetch(
            `/public/admin/sales/${encodeURIComponent(saleId)}/coupon${
                retailerId ? `?${new URLSearchParams({ retailerId }).toString()}` : ''
            }`,
        ),
};
